import { useMutation } from '@apollo/client';

import { gql } from 'src/__generated__';
import type {
  GenerateCoverLetterSuccess,
  GenerateSelectionCriteriaSuccess,
} from 'src/__generated__/graphql';
import {
  uploadCoverLetter,
  uploadSelectionCriteria,
} from 'src/context/upload-attachments';
import { isFile } from 'src/utils';

export function useDocumentsMutations() {
  const [addWrittenCoverLetter] = useMutation(WRITTEN_COVER_LETTER);
  const [addWrittenSelectionCriteria] = useMutation(WRITTEN_SELECTION_CRITERIA);

  async function generateCoverletterUri(coverLetter: string | File) {
    if (isFile(coverLetter)) {
      return uploadCoverLetter({ coverLetter });
    } else if (typeof coverLetter === 'string') {
      const { data } = await addWrittenCoverLetter({
        variables: { input: { writtenText: coverLetter } },
      });

      if (
        data?.generateCoverLetter.__typename === 'GenerateCoverLetterFailure'
      ) {
        throw new Error(data.generateCoverLetter.errors[0].message);
      }

      return (data?.generateCoverLetter as GenerateCoverLetterSuccess).uri;
    }
    throw new Error('Unexpected argument type');
  }

  async function generateSelectionCriteriaUri(
    selectionCriteria: string | File,
  ) {
    if (isFile(selectionCriteria)) {
      return uploadSelectionCriteria({ selectionCriteria });
    } else if (typeof selectionCriteria === 'string') {
      const { data } = await addWrittenSelectionCriteria({
        variables: { input: { writtenText: selectionCriteria } },
      });

      if (
        data?.generateSelectionCriteria?.__typename ===
        'GenerateSelectionCriteriaFailure'
      ) {
        throw new Error(data.generateSelectionCriteria.errors[0].message);
      }

      return (
        data?.generateSelectionCriteria as GenerateSelectionCriteriaSuccess
      ).uri;
    }
    throw new Error('Unexpected argument type');
  }

  return { generateCoverletterUri, generateSelectionCriteriaUri };
}

export const WRITTEN_COVER_LETTER = gql(`
  mutation WrittenCoverLetter($input: GenerateCoverLetterInput!) {
    generateCoverLetter(input: $input) {
      ... on GenerateCoverLetterSuccess {
        uri
      }
      ... on GenerateCoverLetterFailure {
        errors {
          message
        }
      }
    }
  }
`);

export const WRITTEN_SELECTION_CRITERIA = gql(`
  mutation WrittenSelectionCriteria($input: GenerateSelectionCriteriaInput!) {
    generateSelectionCriteria(input: $input) {
      ... on GenerateSelectionCriteriaSuccess {
        uri
      }
      ... on GenerateSelectionCriteriaFailure {
        errors {
          message
        }
      }
    }
  }
`);
